<script setup>
import { displayGameName } from '../../../shared/game-display-name.mjs'
import { createSocketRecovery } from '../services/socket-recovery.mjs'
import { dayRange } from '../../../shared/business-day.js'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { apiConfig, authStorage, request } from '../services/api'
import SharedGameToolbar from '../components/SharedGameToolbar.vue'
import ChatFollowBetModal from '../components/ChatFollowBetModal.vue'
import { useChatFollowBet } from '../services/chat-follow-bet'
import { orderPeriodMessages } from '../services/paijiu-chat'
import BetDetailTime from '../components/BetDetailTime.vue'

const route = useRoute()
const gameInfo = ref(null)
const gameToolbar = ref(null)
const gameState = ref(null)
const userProfile = ref(null)
const todayBill = ref(null)
const odds = ref([])
// 对应旧版 betItems[play_name].bet = { total, member, member_win }。
// 唯一来源：进页面和开奖时由 getStatistic() 覆盖，其余时间由 WebSocket 的
// bet / bet_batch / bet_cancel 消息本地增减。旧版没有任何定时轮询统计接口。
const betTotals = ref({})
const historyRows = ref([])
const drawnResult = ref(null)
// 旧版 setLottery 直接把 collect 消息里的号码写进最近开奖区（lotteryGameNo /
// lotteryNumbers），不重新请求开奖接口；历史列表只在进页面时拉一次。
const liveLastLottery = ref(null)
const selected = ref(new Set())
const showHistory = ref(false)
const showBetBox = ref(false)
const betAmounts = ref({})
const quickAmount = ref('')
const loading = ref(true)
const submitting = ref(false)
const betSucceeded = ref(false)
const showBetSuccessToast = ref(false)
const notice = ref('')
const playType = ref('play')
const chatMessages = ref([])
const orderedChatMessages = computed(() => orderPeriodMessages(chatMessages.value))
const chatContent = ref('')
const chatNewCount = ref(0)
const chatScroll = ref(null)
const chatShow = ref({ bet_me: true, bet_other: true, collect: true, statistic: true, close_check: true })
const cancelConfirmTarget = ref(null)
const showRules = ref(false)
const showGameMenu = ref(false)
const soundEnabled = ref(localStorage.getItem('pjx-sound-enabled') !== 'false')
const soundTip = ref('')
const now = ref(Date.now())
const stateLoadedAt = ref(Date.now())
let clockTimer
// 旧版 lottery_animation_state / lottery_animation_time：开奖后展示 5 秒，
// 第 6 个一秒 tick 时收尾（clearInterval + getState + clearData + 刷余额和今日账单）。
let lotteryAnimationTimer
let lotteryAnimationTime = 5
let socket
let socketRetryTimer
// 旧版 reConnectNumber：只在 close code 1006 时重连，最多 10 次。
let socketRetryCount = 0
let successCloseTimer
let successToastTimer
let enteredAt = 0
let pageLeft = false
const socketRecovery = createSocketRecovery({ connect: () => connectLegacyGameSocket(true), refresh: loadAll, isStopped: () => pageLeft })
let lastChatTimestamp = 0
let soundTipTimer
// 用于识别「刚刚跨过封盘」这一瞬间，对应旧版 setState 里的 close_time <= 0 分支。
let wasBetClosed = false
// 旧版 online：由 connect / close 消息里的 count 维护，显示在「聊天室(N)」的括号里。
const chatOnline = ref(0)
// 旧版重连次数达到 10 次时弹的 alert（header「错误」+ 取消 / 重连 两个按钮）。
const showReconnectAlert = ref(false)
// 旧版充提到账走 appService.showToast，这里用页面内 toast 承载。
const toastMessage = ref('')
let toastTimer
// 旧版在构造函数里从 localStorage 读回 chatCashIds / chatRechargeIds，
// 用来保证同一笔充值/提现的到账提示只弹一次。
function readHandledIds(key) {
  try { return JSON.parse(localStorage.getItem(key) || '[]') } catch (_) { return [] }
}
const handledCashIds = readHandledIds('chatCashIds')
const handledRechargeIds = readHandledIds('chatRechargeIds')

const palette = { 1: 'olive', 2: 'blue', 3: 'black', 4: 'orange', 5: 'teal', 6: 'purple', 7: 'gray', 8: 'red', 9: 'brown', 10: 'green' }
const availableBalance = computed(() => Number(userProfile.value?.balance ?? userProfile.value?.money ?? 0))
const unsettledAmount = computed(() => Number(todayBill.value?.bet_money_uncheck ?? 0))
const todayProfit = computed(() => Number(todayBill.value?.bet_money_pl ?? 0))
const stateElapsed = computed(() => Math.floor((now.value - stateLoadedAt.value) / 1000))
const liveCloseTime = computed(() => Number(gameState.value?.close_time || 0) - stateElapsed.value)
const liveLotteryTime = computed(() => Number(gameState.value?.lottery_time || 0) - stateElapsed.value)
const remaining = computed(() => Math.max(0, liveCloseTime.value))
const isOpen = computed(() => liveCloseTime.value > 0)
const isBetClosed = computed(() => liveCloseTime.value <= 0)
// Exact legacy behaviour from web/29.42d9f906f231ff156120.js:
// close is driven by close_time, while lottery output is driven only by the
// WebSocket `collect` message, never by lottery_state or history polling.
const isSealed = computed(() => liveCloseTime.value <= 0)
const isDrawn = computed(() => drawnResult.value?.balls?.length === 10)
const phaseTimeLabel = computed(() => liveCloseTime.value >= 0 ? '封盘：' : '开奖：')
const phaseTime = computed(() => duration(liveCloseTime.value >= 0 ? liveCloseTime.value : liveLotteryTime.value))
const stateTimeLabel = computed(() => isBetClosed.value ? '开盘：' : '封盘：')
const stateTime = computed(() => duration(isBetClosed.value
  ? Number(gameState.value?.close_time || 0) + Number(gameInfo.value?.game_close_time || 0)
  : remaining.value))
// 旧版 betItems 的键就是 play_name，固定为 a~j 十个门，见 games/*pjx/*Play.php
// （play_name=a..j、play_title=1门..10门、bet_content=1..10）。
const gateNames = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j']
const gates = computed(() => {
  const plays = odds.value.flatMap((type) => type.play || [])
  return gateNames.map((name, index) => {
    const number = index + 1
    const play = plays.find((item) => String(item.play_name) === name)
      || plays.find((item) => String(item.bet_content) === String(number))
      || plays[index]
      || {}
    const bet = betTotals.value[name] || {}
    return {
      name,
      number,
      play,
      total: Number(bet.total || 0),
      mine: Number(bet.member || 0),
      mineWin: Number(bet.member_win || 0),
    }
  })
})
const lastLottery = computed(() => liveLastLottery.value || historyRows.value[0] || null)
const selectedGateEntries = computed(() => gates.value.filter((gate) => selected.value.has(gate.number)).map((gate) => ({ ...gate, amount: Number(betAmounts.value[gate.number] || 0) })))
const totalBetAmount = computed(() => selectedGateEntries.value.reduce((sum, gate) => sum + gate.amount, 0))
const totalPotentialProfit = computed(() => selectedGateEntries.value.reduce((sum, gate) => sum + Math.max(0, gate.amount * (Number(gate.play.bet_odds || 0) - 1)), 0))
const canSubmit = computed(() => isOpen.value && selectedGateEntries.value.length > 0 && selectedGateEntries.value.every((gate) => gate.amount > 0) && !submitting.value && !betSucceeded.value)

function money(value) { return Number(value || 0).toFixed(2) }
/**
 * 旧版 formatTime(n)：n <= 0 返回 "00:00"；不足一小时输出 mm:ss，
 * 满一小时则前置小时段输出 hh:mm:ss。各段不足两位补零。
 */
function formatTime(value) {
  const seconds = Number(value || 0)
  if (seconds <= 0) return '00:00'
  const pad = (input) => (input < 10 ? `0${input}` : String(input))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds / 60) % 60)
  const rest = Math.floor(seconds % 60)
  return `${hours > 0 ? `${pad(hours)}:` : ''}${pad(minutes)}:${pad(rest)}`
}
// 旧版第一个 tab 只在切到聊天室、且尚未封盘时才显示倒计时：
// *ngIf="game.game_state.close_time >= 0 && playType == 'chat'"
const showTabCountdown = computed(() => liveCloseTime.value >= 0 && playType.value === 'chat')
/**
 * 后台游戏列表里的「聊天室」开关（game_chat）。本页原来无条件显示聊天室 tab，
 * 后台那个开关对会员端完全不生效，这里补上。
 * 旧版依据：chunk 24/26/27/28/29/30 的模板里都有 *ngIf="game.game_chat"。
 *
 * 用 Number() 比较而不是直接判真假 —— game_chat 是 tinyint，接口若把它序列化成
 * 字符串 '0'，直接判真假会因为 '0' 是 truthy 而误判成开启。gameInfo 加载完成前
 * 按旧版构造函数的初始值 game_chat:1 处理，默认显示。
 */
const chatEnabled = computed(() => Number(gameInfo.value?.game_chat ?? 1) !== 0)
const ruleHtml = computed(() => {
  const rule = String(gameInfo.value?.game_rule || '').trim()
  return rule ? (/<[a-z][\s\S]*>/i.test(rule) ? rule : rule.replace(/\r?\n/g, '<br>')) : '<p>&#26242;&#26410;&#37197;&#32622;&#28216;&#25103;&#35268;&#21017;&#12290;</p>'
})
function toggleSound() {
  soundEnabled.value = !soundEnabled.value
  localStorage.setItem('pjx-sound-enabled', String(soundEnabled.value))
  soundTip.value = soundEnabled.value ? '\u63d0\u793a\u58f0\u97f3\u5df2\u5f00\u542f' : '\u63d0\u793a\u58f0\u97f3\u5df2\u5173\u95ed'
  window.clearTimeout(soundTipTimer)
  soundTipTimer = window.setTimeout(() => { soundTip.value = '' }, 1600)
}
// 旧版所有倒计时都走同一个 formatTime，因此这里统一委托，避免超过 60 分钟时
// 出现 "61:00" 这种旧版不会出现的写法。
function duration(value) { return formatTime(Math.max(0, Number(value || 0))) }
function numbers(value) {
  if (Array.isArray(value)) return value.map(Number).filter((item) => item >= 1 && item <= 10).slice(0, 10)
  return (String(value || '').match(/\d{1,2}/g) || []).map(Number).filter((item) => item >= 1 && item <= 10).slice(0, 10)
}
function normalizeHistory(row) {
  const formatted = row.format_lottery_numbers || {}
  const result = numbers(formatted.source || row.history_lottery_numbers || row.lottery_numbers)
  const ordered = [...result].sort((left, right) => right - left)
  return {
    no: String(row.game_no || row.history_game_no || row.history_no || ''),
    balls: result,
    // Matches games/jsscpjx/Jsscpjx.php::lotteryResult(..., true): rank is
    // calculated after sorting the ten original gate values from high to low.
    ranks: result.map((ball) => ordered.indexOf(ball) + 1),
  }
}
function resultForGate(number) {
  const index = Number(number) - 1
  return { ball: drawnResult.value?.balls?.[index], rank: drawnResult.value?.ranks?.[index] }
}
function normalizeCollect(payload) {
  const result = payload?.format_lottery_numbers?.result
  if (!Array.isArray(result) || result.length !== 10) return null
  const balls = result.map((item) => numbers(item?.item)[0])
  const ranks = result.map((item) => Number(item?.rank || 0))
  return balls.every((ball) => Number.isInteger(ball)) && ranks.every((rank) => rank > 0)
    ? { no: String(payload.game_no || ''), balls, ranks }
    : null
}
function chatData(message) { return message?.data || {} }
function chatTimestamp(message) {
  const value = Number(message?.time || message?.timestamp || 0)
  return value > 1e12 ? Math.floor(value / 1000) : value
}
function chatTime(value) {
  const stamp = chatTimestamp({ time: value })
  if (!stamp) return ''
  const date = new Date(stamp * 1000)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}
function chatName(message) {
  const data = chatData(message)
  return data.member_nickname || data.member?.member_nickname || data.member?.nickname || data.member_username || data.member?.member_username || data.member?.username || message?.member?.member_nickname || message?.member?.nickname || message?.member?.member_username || message?.member?.username || ''
}
function chatTimeFull(value) {
  const stamp = chatTimestamp({ time: value })
  if (!stamp) return ''
  return `${chatTime(value)}:${String(new Date(stamp * 1000).getSeconds()).padStart(2, '0')}`
}
function chatBets(message) {
  const data = chatData(message)
  return data.lists || data.bet_lists || (Array.isArray(data) ? data : [data])
}
function isMyChatMessage(message) {
  const data = chatData(message)
  const own = [userProfile.value?.username, userProfile.value?.member_username, userProfile.value?.nickname, userProfile.value?.member_nickname].filter(Boolean).map(String)
  const sender = [data.member_username, data.member?.member_username, data.member?.username, data.member_nickname, data.member?.member_nickname, data.member?.nickname, message?.member?.member_username, message?.member?.username, message?.member?.member_nickname, message?.member?.nickname].filter(Boolean).map(String)
  return sender.some((name) => own.includes(name))
}
function chatGameNo(message) { const data = chatData(message); return data.game_no || data.game?.game_no || '' }
function chatAvatar(message) {
  const data = chatData(message)
  const value = data.member_avatar || data.member?.member_avatar || data.member?.avatar || message?.member?.member_avatar || message?.member?.avatar || (isMyChatMessage(message) ? userProfile.value?.avatar : '')
  if (!value || value === '/assets/userface.png') return '/legacy/images/userface-default.png'
  return /^(?:https?:|data:|blob:)/i.test(value) ? value : `https://137n.com${value.startsWith('/') ? value : `/${value}`}`
}
function onChatAvatarError(event) { event.target.onerror = null; event.target.src = '/legacy/images/userface-default.png' }
function chatStatisticName(item) { return item.member_nickname || item.member_username || item.member?.member_nickname || item.member?.nickname || item.member?.member_username || item.member?.username || '' }
function chatStatisticPage(message) { const page = chatData(message).page || {}; return page.total ? `(${page.index || 1}/${page.total})` : '' }
function chatCollectGates(message) {
  const formatted = chatData(message).format_lottery_numbers || {}
  const source = numbers(formatted.source)
  return Array.from({ length: 10 }, (_, index) => ({ gate: index + 1, ball: source[index] }))
}
function chatMessageVisible(message) {
  if (message.type === 'close_check') return chatShow.value.close_check
  if (message.type === 'collect') return chatShow.value.collect
  if (message.type === 'statistic') return chatShow.value.statistic
  if (message.type === 'bet' || message.type === 'bet_batch') return isMyChatMessage(message) ? chatShow.value.bet_me : chatShow.value.bet_other
  return true
}
function toggleChatFilter(key) { chatShow.value = { ...chatShow.value, [key]: !chatShow.value[key] } }
async function scrollChatContent() { await nextTick(); if (chatScroll.value) chatScroll.value.scrollTop = chatScroll.value.scrollHeight }
function appendChatMessage(message) {
  if (!message?.type) return
  if (chatMessages.value.length >= 100) chatMessages.value.splice(0, 1)
  const stamp = chatTimestamp(message)
  if (stamp && stamp - lastChatTimestamp >= 60) chatMessages.value.push({ type: 'time', time: stamp, id: `time-${stamp}` })
  if (stamp) lastChatTimestamp = stamp
  chatMessages.value.push({ ...message, id: message.id || `${message.type}-${stamp || Date.now()}-${Math.random()}` })
  scrollChatContent()
}
function canFollowChatBet(message) { return String(chatGameNo(message)) === String(gameState.value?.game_no || '') && isOpen.value }
const { target: followTarget, busy: followBusy, error: followError, toast: followToast, validation: followValidation, open: openFollow, close: closeFollow, confirm: confirmFollow } = useChatFollowBet({
  balance: availableBalance,
  allowed: (target) => target.game === gameInfo.value?.game_id && canFollowChatBet(target.message) && !chatData(target.message).cancel,
  send: (entries, target) => request('/game/bet/bet_batch', { method: 'POST', data: {
    game: target.game, game_no: target.period,
    bet_lists: entries.map((entry) => ({ play_id: entry.playId, bet_money: Number(entry.amount), bet_content: entry.content })),
  } }),
  afterSuccess: () => Promise.all([refreshTodayBill(), refreshUserMoney()]),
})
function followChatBetBatch(message) {
  if (!canFollowChatBet(message)) return
  openFollow({ message, player: chatName(message), period: chatGameNo(message), game: gameInfo.value?.game_id,
    entries: chatBets(message).map((bet) => {
      const gate = gates.value.find((item) => String(item.play.play_id) === String(bet.play_id))
      return { playId: gate?.play.play_id, content: gate?.play.bet_content, label: gate ? `${gate.number}门` : (bet.play_title || bet.play_name), amount: bet.bet_money ?? bet.money ?? 0 }
    }),
  })
}
function isChatBetCancelable(message) { return isMyChatMessage(message) && String(chatGameNo(message)) === String(gameState.value?.game_no || '') && isOpen.value && !chatData(message).cancel }
function requestChatCancel(message) { if (isChatBetCancelable(message)) cancelConfirmTarget.value = message }
async function confirmChatCancel() {
  const message = cancelConfirmTarget.value
  cancelConfirmTarget.value = null
  if (!message) return
  try {
    const bets = chatBets(message)
    const results = await Promise.all(bets.map((bet) => request('/game/bet/cancel', { method: 'POST', data: { bet_id: bet.bet_id } })))
    if (results.every((item) => Number(item.error_code || 0) === 0)) {
      chatData(message).cancel = true
      bets.forEach((bet) => { bet.cancel = true })
      await refresh()
    }
  } catch (_) { /* preserve the message when cancellation is refused */ }
}
function stopLotteryAnimation() {
  if (lotteryAnimationTimer) window.clearInterval(lotteryAnimationTimer)
  lotteryAnimationTimer = undefined
}
/**
 * 旧版 lotteryAnimation()：每秒递减 lottery_animation_time，归零那一 tick 收尾。
 * 收尾动作固定是 getState() + clearData() + getUserMoney() + getTodayBill() ——
 * 这是大吃小唯一推进到下一期的入口，它的 setState() 不像番摊那样会主动 getState()。
 */
function lotteryAnimation() {
  if (lotteryAnimationTime <= 0) {
    lotteryAnimationTime = 5
    stopLotteryAnimation()
    void loadState()
    clearData()
    void refreshUserMoney()
    void refreshTodayBill()
  }
  lotteryAnimationTime -= 1
}
/**
 * 旧版 setLottery()：开奖号码直接取自 collect 消息，不再请求开奖接口；
 * 同时把 lottery_time 归零、启动 5 秒开奖动画、并重新拉一次期统计。
 */
function setLotteryFromCollect(payload) {
  const result = normalizeCollect(payload)
  if (!result) return
  lotteryAnimationTime = 5
  stopLotteryAnimation()
  drawnResult.value = result
  liveLastLottery.value = { no: result.no, balls: result.balls, ranks: result.ranks }
  if (gameState.value) gameState.value = { ...gameState.value, lottery_time: 0 }
  lotteryAnimationTimer = window.setInterval(lotteryAnimation, 1000)
  void loadStatistic()
  // 旧版 web/29 setLottery：真实开奖进入动画后播放 lottery.mp3。
  gameToolbar.value?.playLottery()
}
/**
 * 旧版 _wsMessage 里 bet / bet_batch / bet_cancel 分支的本地增减：
 * 只处理当期注单，按 play_name 落到对应门上，自己的注单额外累加到 member。
 * clamp 到 0 是本实现加的保护，旧版直接加减。
 */
function applyBetDelta(bet, sign) {
  const name = String(bet?.play_name || '')
  if (!gateNames.includes(name)) return
  if (String(bet.game_no || '') !== String(gameState.value?.game_no || '')) return
  const amount = Number(bet.bet_money || bet.money || 0)
  if (!amount) return
  if (sign < 0) {
    // 旧版撤单时还会核对 play_id 与当前盘口一致才回滚。
    const gate = gates.value.find((item) => item.name === name)
    if (gate?.play?.play_id && bet.play_id && String(gate.play.play_id) !== String(bet.play_id)) return
  }
  const current = betTotals.value[name] || {}
  const isMine = String(bet.member_username || '') === String(userProfile.value?.username || '')
  betTotals.value = {
    ...betTotals.value,
    [name]: {
      total: Math.max(0, Number(current.total || 0) + sign * amount),
      member: isMine ? Math.max(0, Number(current.member || 0) + sign * amount) : Number(current.member || 0),
      member_win: Number(current.member_win || 0),
    },
  }
}
function showToast(message) {
  toastMessage.value = message
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => { toastMessage.value = '' }, 3000)
}
/**
 * 旧版 _wsClose 里「重连」按钮的处理：reConnectNumber 归零、以 reconnect=true
 * 重新连接、并重新 initGame(game_id)。
 */
async function reconnectSocket() {
  showReconnectAlert.value = false
  socketRetryCount = 0
  try { socket?.close(3001, 'exit') } catch (_) { /* 已断开时忽略 */ }
  socket = undefined
  await connectLegacyGameSocket(true)
}
async function connectLegacyGameSocket(reconnect = false) {
  const token = authStorage.getAccessToken()
  const gameId = gameInfo.value?.game_id
  if (!token || !gameId || !apiConfig.wsServer || pageLeft || (socket && socket.readyState <= 1)) return
  // 旧版 _wsOpen：首次连接会清空聊天记录，重连时带 reconnect:1 让服务端补推历史。
  const isReconnect = reconnect
  try { socket = new WebSocket(apiConfig.wsServer) } catch (_) { socketRecovery.retry(); return }
  const activeSocket = socket
  socket.binaryType = 'arraybuffer'
  socket.onopen = () => {
    if (socket !== activeSocket || pageLeft) return
    if (isReconnect) enteredAt = Math.floor(Date.now() / 1000)
    socketRetryCount = 0
    showReconnectAlert.value = false
    if (!isReconnect) {
      chatMessages.value = []
      lastChatTimestamp = 0
    }
    const payload = { type: 'connect', game: gameId, client: 'web-user', token }
    if (isReconnect) payload.reconnect = 1
    socket?.send(JSON.stringify(payload))
    socketRecovery.opened(isReconnect)
  }
  socket.onmessage = (message) => {
    try {
      const response = JSON.parse(message.data)
      const event = response?.error_code === 0 ? response.data : null
      if (!event?.type) return
      // 旧版用 parseInt(t.time) 与 inTimestamp 比较，把连接后补推的历史消息
      // 排除在本地增减之外，避免和初始 getStatistic 的结果重复计算。
      const eventTime = chatTimestamp(event)
      const isLive = eventTime > enteredAt
      if (event.type === 'connect' || event.type === 'online' || event.type === 'close') {
        chatOnline.value = Number(chatData(event).count || 0)
        return
      }
      if (event.type === 'collect') {
        if (isLive) setLotteryFromCollect(event.data)
        appendChatMessage(event)
        return
      }
      if (event.type === 'bet') {
        // 单注（包括机器人下注）和批量下注都显示，盘口金额仍只累加一次。
        appendChatMessage(event)
        if (playType.value !== 'chat') chatNewCount.value += 1
        if (isLive) applyBetDelta(chatData(event), 1)
        return
      }
      if (event.type === 'bet_cancel') {
        // 旧版顺序：先无条件标记聊天流里的注单，再判断实时性回滚金额。
        const cancelled = chatData(event).bet_id
        chatMessages.value.forEach((item) => chatBets(item).forEach((bet) => {
          if (String(bet.bet_id) === String(cancelled)) { bet.cancel = true; chatData(item).cancel = true }
        }))
        if (isLive) applyBetDelta(chatData(event), -1)
        return
      }
      if (['bet_batch', 'chat', 'statistic', 'close_check'].includes(event.type)) {
        appendChatMessage(event)
        if (playType.value !== 'chat') chatNewCount.value += 1
        if (event.type === 'bet_batch' && isLive) chatBets(event).forEach((bet) => applyBetDelta(bet, 1))
        // 旧版 statistic 分支只刷余额和今日账单。
        if (event.type === 'statistic') {
          void refreshUserMoney()
          void refreshTodayBill()
        }
        return
      }
      if (['recharge_handle', 'cash_handle', 'cash_cancel'].includes(event.type)) {
        // 旧版按 recharge_id / cash_id 在 localStorage 里去重，只提示一次，
        // 并且只有实时消息才刷余额。
        const data = chatData(event)
        const isCash = event.type !== 'recharge_handle'
        const storageKey = isCash ? 'chatCashIds' : 'chatRechargeIds'
        const id = isCash ? data.cash_id : data.recharge_id
        const seen = isCash ? handledCashIds : handledRechargeIds
        if (id === undefined || seen.includes(id)) return
        seen.push(id)
        try { localStorage.setItem(storageKey, JSON.stringify(seen)) } catch (_) { /* 存储不可用时只影响去重 */ }
        showToast(event.type === 'recharge_handle'
          ? '您的充值申请已处理'
          : event.type === 'cash_handle'
            ? '您的提现申请已处理'
            : '您有提现申请已补取消，请息知')
        if (isLive) void refreshUserMoney()
      }
    } catch (_) { /* retain the active legacy view */ }
  }
  socket.onclose = (event) => {
    if (socket !== activeSocket) return
    socket = undefined
    socketRetryCount += 1
    socketRecovery.retry(event)
  }
  socket.onerror = () => { try { activeSocket.close() } catch (_) {} }
}
function selectChatBlock(type) {
  playType.value = type
  if (type === 'chat') { chatNewCount.value = 0; scrollChatContent() }
}
function sendChatMessage() {
  const message = chatContent.value.trim()
  if (!message || !socket || socket.readyState !== WebSocket.OPEN) return
  socket.send(JSON.stringify({ type: 'chat', game: gameInfo.value?.game_id || route.params.game, message }))
  chatContent.value = ''
}
function color(number) { return palette[Number(number)] || 'gray' }
function toggleGate(gate) {
  if (!isOpen.value || !gate.play?.play_id) return
  const next = new Set(selected.value)
  next.has(gate.number) ? next.delete(gate.number) : next.add(gate.number)
  selected.value = next
}
function resetSelection() {
  selected.value = new Set()
  betAmounts.value = {}
  quickAmount.value = ''
  betSucceeded.value = false
  notice.value = ''
}
function dismissSuccessfulBet() {
  window.clearTimeout(successCloseTimer)
  showBetBox.value = false
  selected.value = new Set()
  betAmounts.value = {}
  quickAmount.value = ''
  betSucceeded.value = false
  notice.value = ''
}
function removeGate(number) {
  const next = new Set(selected.value)
  next.delete(number)
  selected.value = next
  const nextAmounts = { ...betAmounts.value }
  delete nextAmounts[number]
  betAmounts.value = nextAmounts
  if (!next.size) showBetBox.value = false
}
function applyQuickAmount() {
  const value = Number(quickAmount.value || 0)
  if (!value) return
  betAmounts.value = Object.fromEntries(selectedGateEntries.value.map((gate) => [gate.number, value]))
}
// 旧版所有接口都用 game_id 传参（getGameState / getGameOdds / getStatistic /
// getTodayBill / getLotteryList 全部是 this.game.game_id）。这一点对
// /game/bet/today 和 /game/bet/lists 是硬性要求 —— 后端 GameBet::getBillData()
// 是 where('game_id', $game)，传 game_name 会查不到任何数据。
const gameKey = computed(() => gameInfo.value?.game_id || route.params.game)

// 对应旧版 clearData()：盘口金额归零、开奖态复位。
function clearData() {
  betTotals.value = {}
  drawnResult.value = null
}
async function refreshUserMoney() {
  try {
    const response = await request('/user/money')
    userProfile.value = response.data || userProfile.value
  } catch (_) { /* 保留上一次成功读取的余额 */ }
}
async function refreshTodayBill() {
  try {
    const response = await request('/game/bet/today', { method: 'POST', data: { game: gameKey.value } })
    todayBill.value = response.data || todayBill.value
  } catch (_) { /* 保留上一次成功读取的今日账单 */ }
}
async function loadState() {
  const response = await request('/game/state', { method: 'POST', data: { game: gameKey.value } })
  const nextState = response.data || null
  // 旧版 getState()：期号变化时只做 getTodayBill() + clearData()。
  const gameNoChanged = nextState?.game_no && nextState.game_no !== gameState.value?.game_no
  gameState.value = nextState
  stateLoadedAt.value = Date.now()
  if (gameNoChanged) {
    refreshTodayBill()
    clearData()
    selected.value = new Set()
    betAmounts.value = {}
  }
}
async function loadStatistic() {
  if (!gameState.value?.game_no) return
  const response = await request('/game/statistic', { method: 'POST', data: { game: gameKey.value, game_no: gameState.value.game_no, member: 1 } })
  // 旧版 getStatistic 按 play_id 把结果写回 betItems[play_name].bet，
  // total 会整体重算（total_bet = 0 后累加），这里等价地整表覆盖。
  const plays = odds.value.flatMap((type) => type.play || [])
  const playNameOf = (playId) => plays.find((item) => String(item.play_id) === String(playId))?.play_name
  const next = {}
  ;(response.data?.total || []).forEach((item) => {
    const name = playNameOf(item.play_id)
    if (!name) return
    next[name] = { ...(next[name] || {}), total: Number(item.bet_money || 0) }
  })
  ;(response.data?.member || []).forEach((item) => {
    const name = playNameOf(item.play_id)
    if (!name) return
    next[name] = { ...(next[name] || {}), member: Number(item.bet_money || 0), member_win: Number(item.bet_money_win || 0) }
  })
  betTotals.value = next
}
// 注意：大吃小的开奖历史列表在旧版里只有 initGame 的 getLottery() 会拉一次，
// 开奖时靠 collect 消息直接更新最近开奖区，不重新请求历史，因此这里没有
// 单独的 loadHistory —— 拉取内联在 loadAll 中。
// 对应旧版 initGame()：getLottery() → getState() → getOdds() → getTodayBill()
// → getUserMoney() → getStatistic()，一次性拉全，之后不再定时轮询。
async function loadAll() {
  const list = await request('/game/lists')
  gameInfo.value = (list.data?.lists || []).find((item) => String(item.game_id) === String(route.params.game)) || null
  const game = gameKey.value
  const [state, oddsResult, money, bill, history] = await Promise.all([
    request('/game/state', { method: 'POST', data: { game } }),
    request('/game/odds', { method: 'POST', data: { game } }),
    request('/user/money'),
    request('/game/bet/today', { method: 'POST', data: { game } }),
    request('/game/history', { method: 'POST', data: { game, ...dayRange(), pagesize: gameInfo.value?.game_history_count || 25 } }),
  ])
  gameState.value = state.data || null
  stateLoadedAt.value = Date.now()
  odds.value = oddsResult.data || []
  userProfile.value = money.data || null
  todayBill.value = bill.data || null
  const rows = history.data?.lists || history.data?.history || history.data || []
  historyRows.value = Array.isArray(rows) ? rows.map(normalizeHistory).filter((item) => item.no) : []
  await loadStatistic()
}
// 期号栏那个 ↻ 只是手动刷新入口，对应旧版工具栏的刷新动作：重新拉盘态和期统计。
async function refresh() {
  try { await loadState(); await loadStatistic() } catch (_) { /* 保留上一次成功的盘面数据 */ }
}
async function submit() {
  const selectedGates = selectedGateEntries.value
  if (!canSubmit.value || betSucceeded.value) return
  submitting.value = true
  notice.value = ''
  try {
    await request('/game/bet/bet_batch', { method: 'POST', data: {
      game: gameInfo.value?.game_id,
      game_no: gameState.value.game_no,
      bet_lists: selectedGates.map((gate) => ({
        play_id: gate.play.play_id,
        bet_money: gate.amount,
        bet_content: gate.play.bet_content,
      })),
    } })
    betSucceeded.value = true
    showBetSuccessToast.value = true
    window.clearTimeout(successCloseTimer)
    window.clearTimeout(successToastTimer)
    successCloseTimer = window.setTimeout(dismissSuccessfulBet, 2000)
    successToastTimer = window.setTimeout(() => { showBetSuccessToast.value = false }, 3500)
    // 旧版 betBoxPost 成功后只调 getTodayBill() 和 getUserMoney()；自己这笔注
    // 会由服务端通过 WebSocket 的 bet_batch 推回来，盘口金额在那里本地累加。
    await Promise.all([refreshTodayBill(), refreshUserMoney()])
  } catch (error) { notice.value = error.message || '投注失败' } finally { submitting.value = false }
}

onMounted(async () => {
  enteredAt = Math.floor(Date.now() / 1000)
  try { await loadAll() } catch (error) { notice.value = error.message || '加载失败' } finally { loading.value = false }
  connectLegacyGameSocket()
  // 旧版 setState() 每秒只递减本地倒计时，不请求任何接口；期号推进完全依赖
  // WebSocket 的 collect 消息触发的开奖动画收尾。这里的 clockTimer 起同样作用
  // 倒计时仍在本地推进；启用开奖保护时，另外每10秒核对后端盘态。
  let guardTicks = 0
  let guardRefreshing = false
  clockTimer = window.setInterval(() => {
    now.value = Date.now()
    if (++guardTicks % 10 === 0 && gameState.value?.draw_guard?.enabled && !guardRefreshing) {
      guardRefreshing = true
      void loadState().catch(() => {}).finally(() => { guardRefreshing = false })
    }
    // 旧版 setState()：close_time <= 0 那一刻执行 closeBetPostBox()，并把所有
    // 格子的 checked 置为 false，也就是封盘会清掉已选注并关闭下注弹层。
    if (isBetClosed.value && !wasBetClosed) {
      wasBetClosed = true
      showBetBox.value = false
      selected.value = new Set()
      betAmounts.value = {}
      quickAmount.value = ''
      betSucceeded.value = false
    } else if (!isBetClosed.value) {
      wasBetClosed = false
    }
  }, 1000)
})
onUnmounted(() => {
  socketRecovery.dispose()
  pageLeft = true
  window.clearInterval(clockTimer)
  stopLotteryAnimation()
  window.clearTimeout(socketRetryTimer)
  window.clearTimeout(successCloseTimer)
  window.clearTimeout(successToastTimer)
  window.clearTimeout(soundTipTimer)
  window.clearTimeout(toastTimer)
  socket?.close(3001, 'exit')
})
</script>

<template>
  <ChatFollowBetModal :target="followTarget" :balance="availableBalance" :busy="followBusy" :error="followError" :validation="followValidation" :toast="followToast" @close="closeFollow" @confirm="confirmFollow" />
  <main class="pjx-page">
    <SharedGameToolbar ref="gameToolbar" :game-info="gameInfo" :draw-guard="gameState?.draw_guard" :user-profile="userProfile" :available-balance="availableBalance" />
    <header class="pjx-game-toolbar">
      <RouterLink to="/home" class="pjx-toolbar-back" aria-label="&#36820;&#22238;"><svg viewBox="0 0 512 512" aria-hidden="true"><path d="M244 400 100 256l144-144M120 256h292" /></svg></RouterLink>
      <strong>{{ gameInfo?.game_title || '&#28595;&#27954;&#24184;&#36816;10' }}</strong>
      <div class="pjx-toolbar-actions" aria-label="&#24037;&#20855;&#26639;"><button type="button" :aria-label="soundEnabled ? '&#20851;&#38381;&#25552;&#31034;&#22768;&#38899;' : '&#24320;&#21551;&#25552;&#31034;&#22768;&#38899;'" @click="toggleSound"><svg v-if="soundEnabled" viewBox="0 0 512 512" aria-hidden="true"><path d="M256 448a48 48 0 0 0 47.9-43.4H208.1A48 48 0 0 0 256 448Zm130.2-115.5V223.1c0-61.4-40.4-113.3-96.2-131.3V72a34.1 34.1 0 0 0-68.2 0v19.8c-55.8 18-96.2 69.9-96.2 131.3v109.4l-39.7 39.7v20.5h340.6v-20.5l-40.3-39.7Z" /></svg><svg v-else viewBox="0 0 512 512" aria-hidden="true"><path d="M207.8 400c0 27 21.6 48 48.1 48s48.1-21 48.1-48h-96.2zM133.8 32L120 40.1 378.2 480l13.8-8.1zM373.9 229c0-99.1-49-132.9-94.9-140.6 0-.6.1-1.1.1-1.7 0-12.7-10.3-23-23-23s-23 10.3-23 23c0 .6 0 1.2.1 1.8-13.1 2.2-26.5 6.6-38.9 14L359 383.9h73c-35.4-32-58.1-39-58.1-154.9zM138.1 229.2C138.1 345.1 115.6 352 80 384h222.9L159 138c-12.8 21.4-20.9 50.9-20.9 91.2z" /></svg></button><button type="button" aria-label="&#28216;&#25103;&#35268;&#21017;" @click="showRules = true"><img src="/legacy/svg/ios-paper.svg" alt=""></button><button type="button" aria-label="&#33756;&#21333;" @click="showGameMenu = true"><img src="/legacy/svg/ios-menu.svg" alt=""></button></div>
    </header>
    <header class="pjx-toolbar">
      <RouterLink to="/home" aria-label="返回">‹</RouterLink>
      <strong>{{ gameInfo?.game_title || '极速赛车' }}</strong>
      <div class="toolbar-tools"><svg viewBox="0 0 512 512" aria-label="静音"><path d="M256 448a48 48 0 0 0 47.9-43.4H208.1A48 48 0 0 0 256 448Zm130.2-115.5V223.1c0-61.4-40.4-113.3-96.2-131.3V72a34.1 34.1 0 0 0-68.2 0v19.8c-55.8 18-96.2 69.9-96.2 131.3v109.4l-39.7 39.7v20.5h340.6v-20.5l-40.3-39.7Z" /></svg><button type="button" aria-label="规则"><img src="/legacy/svg/ios-paper.svg" alt=""></button><button type="button" aria-label="菜单">☰</button></div>
    </header>

    <!--
      对齐旧版 play-type 栏：第一个 tab 是 game_type，倒计时只在切到聊天室且未封盘时显示；
      第二个 tab 括号里是在线人数（旧版 online），未读数是旁边的徽章，>=100 显示 99+。
    -->
    <!--
      结构对齐旧版 play-type：外层 item 负责占位和居中，内层 span 才是带下边框的元素，
      所以选中态的红线宽度 = 文字宽度 + span 的左右各 20px padding，而不是固定比例。
      倒计时 span.time 嵌在同一个内层 span 里，因此也被红线覆盖。
    -->
    <nav class="pjx-tabs pjx-chat-tabs">
      <button type="button" :class="{ active: playType === 'play' }" @click="selectChatBlock('play')">
        <span>{{ displayGameName(gameInfo?.game_type || '') }}<span v-if="showTabCountdown" class="time">({{ formatTime(liveCloseTime) }})</span></span>
      </button>
      <!-- v-if="chatEnabled"：后台「聊天室」开关关掉时不显示这个 tab（旧版 *ngIf="game.game_chat"） -->
      <button v-if="chatEnabled" type="button" :class="{ active: playType === 'chat' }" @click="selectChatBlock('chat')">
        <span>聊天室({{ chatOnline }})<b v-if="chatNewCount > 0 && chatNewCount < 100">{{ chatNewCount }}</b><b v-else-if="chatNewCount >= 100">99+</b></span>
      </button>
    </nav>

    <nav class="pjx-tabs"><button class="active">十门</button><button>聊天室(2)<b>2</b></button></nav>
    <section class="account-strip"><span>可用金额：<b>{{ money(availableBalance) }}</b></span><span>未结金额：<em>{{ money(unsettledAmount) }}</em></span><span>今日输赢：<em>{{ money(todayProfit) }}</em></span></section>

    <!-- || !chatEnabled 是兜底：聊天室开关被关掉时，即便 playType 还停在 'chat' 也要显示盘面 -->
    <div v-if="playType === 'play' || !chatEnabled" class="pjx-scroll">
      <section class="draw-area">
        <div class="draw-head"><span>{{ lastLottery?.no || '—' }}期</span></div>
        <div class="draw-balls"><div v-for="(ball, index) in (lastLottery?.balls || [])" :key="`${ball}-${index}`" class="draw-slot"><span>{{ index + 1 }}门</span><i :class="color(ball)">{{ String(ball).padStart(2, '0') }}</i></div></div>
      </section>

      <section v-if="showHistory" class="history-panel">
        <article v-for="item in historyRows" :key="item.no"><span>{{ item.no }}</span><div><i v-for="(ball, index) in item.balls" :key="`${item.no}-${index}`" :class="color(ball)">{{ String(ball).padStart(2, '0') }}</i></div></article>
      </section>

      <section class="period-bar"><span>{{ gameState?.game_no || '—' }}期</span><b>总投注：<em>{{ money(gates.reduce((sum, gate) => sum + gate.total, 0)) }}</em></b><span>{{ phaseTimeLabel }} {{ phaseTime }} <button type="button" @click="refresh">↻</button></span></section>

      <section :class="['gate-board', { 'is-sealed': isSealed, 'is-drawn': isDrawn }]">
        <button v-for="gate in gates" :key="gate.number" type="button" :class="{ selected: selected.has(gate.number) }" @click="toggleGate(gate)">
          <strong>{{ gate.number }}门</strong><small>♣ {{ money(gate.total) }}</small><small>♟ {{ money(gate.mine) }} / {{ money(gate.mineWin) }}</small><i v-if="selected.has(gate.number)">✓</i>
          <div v-if="isDrawn" class="gate-draw-result">
            <i :class="color(resultForGate(gate.number).ball)">{{ String(resultForGate(gate.number).ball).padStart(2, '0') }}</i>
            <small>{{ money(gate.total) }}</small>
            <b>第{{ resultForGate(gate.number).rank }}名</b>
            <span class="gate-draw-member">&#9823; {{ money(gate.mine) }} / {{ money(gate.mineWin) }}</span>
          </div>
        </button>
      </section>

      <section v-if="!showHistory" class="history-panel compact-history">
        <article v-for="item in historyRows" :key="item.no"><span>{{ item.no }}</span><div><i v-for="(ball, index) in item.balls" :key="`${item.no}-${index}`" :class="color(ball)">{{ String(ball).padStart(2, '0') }}</i></div></article>
      </section>
    </div>

    <footer v-if="selected.size" class="bet-action"><button type="button" @click="showBetBox = true">立即下注</button><button type="button" class="reset" @click="resetSelection">重 置</button></footer>
    <section v-if="showBetBox" class="bet-confirm-mask" @click.self="!betSucceeded && (showBetBox = false)"><div class="bet-confirm-dialog"><header><span>可用：<b>{{ money(availableBalance) }}</b></span><span>{{ duration(remaining) }}</span><button type="button" aria-label="关闭" @click="dismissSuccessfulBet">×</button></header><div class="confirm-quick"><span>快速金额：</span><input v-model="quickAmount" :disabled="betSucceeded" inputmode="decimal" placeholder="快速金额" @change="applyQuickAmount" @keyup.enter="applyQuickAmount"></div><div class="confirm-head"><b>号码</b><b>金额</b><b>可赢</b><b>状态</b></div><div v-for="gate in selectedGateEntries" :key="gate.number" class="confirm-row"><span>{{ gate.number }}门</span><input v-model="betAmounts[gate.number]" :disabled="betSucceeded" inputmode="decimal" placeholder="0"><span>{{ money(Math.max(0, gate.amount * (Number(gate.play.bet_odds || 0) - 1))) }}</span><button v-if="!betSucceeded" type="button" :aria-label="`删除${gate.number}门`" @click="removeGate(gate.number)">×</button><span v-else class="confirm-success-check" aria-label="投注成功">✓</span></div><p v-if="notice" class="confirm-error">{{ notice }}</p><div class="confirm-summary"><span>投注数：<b>{{ selectedGateEntries.length }}</b></span><span>总金额：<b>{{ money(totalBetAmount) }}</b></span><span>可盈利：<b>{{ money(totalPotentialProfit) }}</b></span></div><button type="button" :class="['confirm-submit', { success: betSucceeded }]" :disabled="!betSucceeded && !canSubmit" @click.prevent="betSucceeded ? dismissSuccessfulBet() : submit()">{{ betSucceeded ? '下注成功，请确认' : (submitting ? '投注中…' : '确定下注') }}</button></div></section>
    <div v-if="showBetSuccessToast" class="bet-success-toast" role="status">投注成功</div>
    <p v-if="loading" class="page-state">加载中…</p>
    <p v-else-if="notice && !showBetBox" class="page-notice">{{ notice }}</p>
    <section v-if="chatEnabled && playType === 'chat'" class="pjx-chat-content">
      <div ref="chatScroll" class="pjx-chat-container">
        <article v-for="message in orderedChatMessages" v-show="chatMessageVisible(message)" :key="message.id" :class="['pjx-msg-item', `pjx-msg-${message.type}`]">
          <p v-if="message.type === 'time'" class="pjx-msg-time">{{ chatTime(message.time) }}</p>
          <template v-else-if="message.type === 'chat'">
            <div :class="['pjx-chat-message', isMyChatMessage(message) ? 'pjx-chat-me' : 'pjx-chat-other']"><img class="pjx-chat-avatar" :src="chatAvatar(message)" alt="" @error="onChatAvatarError"><div class="pjx-chat-msg-content"><p class="pjx-chat-nickname">{{ chatName(message) }}</p><p class="pjx-chat-msg-text">{{ chatData(message).message || chatData(message).content }}</p></div></div>
          </template>
          <template v-else-if="message.type === 'bet' || message.type === 'bet_batch'">
            <div :class="['pjx-chat-message', isMyChatMessage(message) ? 'pjx-chat-me' : 'pjx-chat-other']"><img class="pjx-chat-avatar" :src="chatAvatar(message)" alt="" @error="onChatAvatarError"><div class="pjx-chat-msg-content"><p class="pjx-chat-nickname">{{ chatName(message) }}</p><div class="pjx-bet-msg"><p>&#25105;&#21018;&#21018;&#25237;&#20102; <b>{{ chatGameNo(message) }}</b> &#26399; {{ chatBets(message).length }} &#27880;</p><div class="pjx-bet-msg-detail"><div class="pjx-bet-lines"><div v-for="bet in chatBets(message)" :key="bet.bet_id || bet.play_id" class="pjx-bet-line"><span>{{ bet.play_title || bet.play_name || bet.bet_content }}&#38376;&#25237;&#20102;&#65306;<b>{{ money(bet.bet_money || bet.money) }}</b></span></div></div><div class="pjx-bet-action"><button v-if="isMyChatMessage(message)" type="button" :disabled="!isChatBetCancelable(message) || chatData(message).cancel" @click="requestChatCancel(message)">{{ chatData(message).cancel ? '&#24050;&#21462;&#28040;' : '&#21462;&#28040;' }}</button><button v-else type="button" :disabled="!canFollowChatBet(message) || chatData(message).cancel" @click="followChatBetBatch(message)">{{ chatData(message).cancel ? '&#24050;&#21462;&#28040;' : '&#36319;&#25237;' }}</button></div></div></div></div></div>
          </template>
          <section v-else-if="message.type === 'collect'" class="pjx-msg-card pjx-collect-card"><strong>&#31532; {{ chatData(message).game_no }} &#26399;&#24320;&#22870;&#20844;&#21578;</strong><div class="pjx-collect-gates"><span v-for="item in chatCollectGates(message)" :key="item.gate"><i>{{ item.gate }}&#38376;</i><b :class="color(item.ball)">{{ String(item.ball || '').padStart(2, '0') }}</b></span></div></section>
          <section v-else-if="message.type === 'statistic'" class="pjx-msg-card pjx-statistic-card"><strong>&#31532; {{ chatGameNo(message) }} &#26399;&#24320;&#22870;&#25253;&#34920;{{ chatStatisticPage(message) }}</strong><div class="pjx-stat-head"><b>&#26165;&#31216;</b><b>&#19979;&#27880;</b><b>&#20013;&#22870;</b><b>&#30408;&#21033;</b></div><div v-for="item in chatData(message).lists || []" :key="item.member_id || chatStatisticName(item)" class="pjx-stat-row"><span>{{ chatStatisticName(item) }}</span><span class="pjx-stat-money">{{ money(item.bet_money) }}</span><span class="pjx-stat-money">{{ money(item.bet_money_win ?? item.win_money) }}</span><span :class="Number(item.bet_money_pl ?? item.pl_money) < 0 ? 'pjx-stat-loss' : 'pjx-stat-money'">{{ money(item.bet_money_pl ?? item.pl_money) }}</span></div></section>
          <section v-else-if="message.type === 'close_check'" class="pjx-msg-card pjx-close-check-card">
            <strong>第 {{ chatGameNo(message) }} 期</strong>
            <p class="pjx-close-check-total">核对列表【本期下注总金额：{{ money(chatData(message).total_money) }}】</p>
            <div v-for="gate in chatData(message).gates || []" :key="gate.gate" class="pjx-close-check-gate">
              <b>【{{ gate.gate }}门总投：{{ money(gate.total_money) }}】</b>
              <p v-if="!gate.rows?.length">无</p>
              <p v-for="row in gate.rows || []" :key="`${row.member_id}-${row.bet_create_time}`" class="pjx-close-check-row"><span>{{ row.member_name }} - 投：{{ money(row.bet_money) }}</span><BetDetailTime :row="row" :rows="gate.rows" :format-time="chatTimeFull" /></p>
            </div>
          </section>
        </article>
      </div>
      <footer class="pjx-chat-footer"><div class="pjx-chat-filters"><button :class="{ checked: chatShow.bet_me }" @click="toggleChatFilter('bet_me')">&#25105;&#30340;&#25237;&#27880;</button><button :class="{ checked: chatShow.bet_other }" @click="toggleChatFilter('bet_other')">&#20182;&#20154;&#25237;&#27880;</button><button :class="{ checked: chatShow.collect }" @click="toggleChatFilter('collect')">&#24320;&#22870;</button><button :class="{ checked: chatShow.statistic }" @click="toggleChatFilter('statistic')">&#25253;&#34920;</button><button :class="{ checked: chatShow.close_check }" @click="toggleChatFilter('close_check')">核对列表</button></div><div class="pjx-chat-send"><input v-model="chatContent" type="text" maxlength="1000" placeholder="&#35831;&#36755;&#20837;&#24744;&#35201;&#21457;&#36865;&#30340;&#20869;&#23481;" @keyup.enter="sendChatMessage"><button type="button" :disabled="!chatContent.trim()" @click="sendChatMessage">&#21457;&#36865;</button></div></footer>
    </section>
    <section v-if="cancelConfirmTarget" class="pjx-cancel-mask"><div class="pjx-cancel-dialog"><h2>&#35831;&#30830;&#35748;</h2><p>&#24744;&#30830;&#35748;&#21462;&#28040;&#21527;&#65311;</p><footer><button type="button" @click="cancelConfirmTarget = null">&#20851;&#38381;</button><button type="button" @click="confirmChatCancel">&#30830;&#35748;</button></footer></div></section>
    <!-- 旧版 _wsClose 在重连 10 次失败后弹的 alert：header「错误」+ 取消 / 重连。 -->
    <section v-if="showReconnectAlert" class="pjx-cancel-mask">
      <div class="pjx-cancel-dialog">
        <h2>错误</h2>
        <p>您与服务器已断开连接，请重新连接</p>
        <footer>
          <button type="button" @click="showReconnectAlert = false">取消</button>
          <button type="button" @click="reconnectSocket">重连</button>
        </footer>
      </div>
    </section>
    <div v-if="toastMessage" class="bet-success-toast" role="status">{{ toastMessage }}</div>
    <div class="pjx-toolbar-action-overlays" aria-label="&#24037;&#20855;&#26639;"><button type="button" :aria-label="soundEnabled ? '&#20851;&#38381;&#25552;&#31034;&#22768;&#38899;' : '&#24320;&#21551;&#25552;&#31034;&#22768;&#38899;'" @click="toggleSound"></button><button type="button" aria-label="&#28216;&#25103;&#35268;&#21017;" @click="showRules = true"></button><button type="button" aria-label="&#33756;&#21333;" @click="showGameMenu = true"></button></div>
    <section v-if="showRules" class="pjx-tool-page" aria-label="&#28216;&#25103;&#35268;&#21017;"><header><button type="button" aria-label="&#36820;&#22238;" @click="showRules = false"><svg viewBox="0 0 512 512" aria-hidden="true"><path d="M244 400 100 256l144-144M120 256h292" /></svg></button><b>&#28216;&#25103;&#35268;&#21017;</b><span></span></header><main v-html="displayGameName(ruleHtml)"></main></section>
    <!-- 本页原来自带的一份「个人中心」抽屉已移除（它的触发按钮本就被 CSS 隐藏，属于死 DOM）。
         抽屉统一由 SharedGameToolbar 渲染的 SharedPersonalCenterDrawer 承担。 -->
    <div v-if="soundTip" class="pjx-sound-tip" role="status">{{ soundTip }}</div>
  </main>
</template>

<style scoped>
.pjx-page{min-height:100vh;padding-bottom:10px;background:#f4f4f4;color:#111;font-family:Arial,"Microsoft YaHei",sans-serif;font-size:12px}.pjx-toolbar{display:grid;grid-template-columns:44px 1fr 104px;height:51px;align-items:center;background:#ff545b;box-shadow:0 2px 4px #0003;color:#fff}.pjx-toolbar>a{padding-left:16px;color:#fff;font-size:32px;line-height:1;text-decoration:none}.pjx-toolbar strong{text-align:center;font-size:18px;font-weight:600}.toolbar-tools{display:flex;align-items:center;justify-content:space-around;font-size:21px}.toolbar-tools button{border:0;background:transparent;color:#fff;font-size:21px}/* 逐条对应旧版 .play-type 区块：容器 flex + padding-bottom .2rem + 1px #e5e5e5 底线；
   item width:100% / text-align:center / padding:5px；内层 span font-size:.8rem /
   inline-block / padding:5px 20px；选中态给 item 上色、给内层 span 加 2px 下边框。
   红主题下 --ion-color-primary 是 #f95454（main.js 主题模块 XAiG 覆盖了默认的 #3880ff）。 */
.pjx-tabs{display:flex;align-items:center;padding-bottom:.2rem;border-bottom:1px solid #e5e5e5;background:#fff;font-size:1rem}
.pjx-tabs button{width:100%;border:0;background:transparent;padding:5px;color:inherit;font:inherit;text-align:center}
.pjx-tabs button>span{display:inline-block;padding:5px 20px;font-size:.8rem}
.pjx-tabs .active{color:#f95454}
.pjx-tabs .active>span{border-bottom:2px solid #f95454}
.pjx-tabs .time{padding:0}
.pjx-tabs b{display:inline-grid;width:16px;height:19px;margin-left:4px;place-items:center;border-radius:4px;background:red;color:#fff;font-size:12px}.account-strip{display:flex;justify-content:space-between;padding:5px 12px;background:#d9f8fc;color:#333;font-size:12px}.account-strip b{color:#f04141;font-weight:400}.account-strip em{color:#777;font-style:normal}.draw-area{background:#fff}.draw-head{display:flex;height:35px;align-items:center;justify-content:space-between;padding:0 14px;color:#e8353d;font-size:13px}.draw-head button{border:0;background:transparent;color:#888;font-size:18px}.draw-balls{display:flex;min-height:77px;justify-content:space-around;padding:8px 18px 12px}.draw-slot{display:grid;justify-items:center;gap:3px;color:#111;font-size:13px;line-height:1}.draw-balls i,.history-panel i{display:inline-grid;place-items:center;border-radius:5px;color:#fff;font-style:normal}.draw-balls i{width:31px;height:32px;font-size:17px}.olive{background:#d8dc00}.blue{background:#008ed4}.black{background:#343434}.orange{background:#f06a00}.teal{background:#0ccbd1}.purple{background:#4c2ee8}.gray{background:#b5b7b7}.red{background:#ef2b16}.brown{background:#850a03}.green{background:#00a90d}.period-bar{display:grid;grid-template-columns:1fr 1fr 1fr;height:38px;align-items:center;padding:0 14px;border-top:1px solid #e5e5e5;background:#fff;color:#444}.period-bar b{text-align:center;font-weight:400}.period-bar em{color:#e6373d;font-size:15px;font-style:normal}.period-bar>span:last-child{text-align:right}.period-bar button{border:0;background:transparent;color:#c93038;font-size:16px}.gate-board{display:grid;grid-template-columns:1fr 1fr;gap:18px 34px;padding:18px 25px;background:#389b61}.gate-board button{position:relative;display:grid;min-height:60px;place-content:center;gap:3px;border:1px solid #77c697;background:#389b61;color:#fff}.gate-board strong{font-size:14px;font-weight:600}.gate-board small{color:#fff;font-size:12px}.gate-board small:last-of-type{color:#ffe561}.gate-board .selected{border-color:#ffe372;background:#24804e;color:#ffe372;box-shadow:inset 0 0 0 1px #ffe372}.gate-board .selected small{color:#e4c343}.gate-board i{position:absolute;right:6px;bottom:4px;color:#ffe372;font-size:17px;font-style:normal}.history-panel{background:#fff}.history-panel article{display:grid;grid-template-columns:75px 1fr;min-height:33px;align-items:center;padding:0 11px;border-bottom:1px solid #e5e5e5}.history-panel article>span{font-size:12px}.history-panel article>div{display:flex;justify-content:space-between;gap:4px}.history-panel i{width:24px;height:24px;font-size:11px}.compact-history{max-height:320px;overflow-y:auto}.bet-footer{position:fixed;right:0;bottom:0;left:0;z-index:20;display:flex;max-width:640px;height:52px;margin:auto;align-items:center;justify-content:space-between;padding:0 14px;background:#fff;box-shadow:0 -2px 5px #0002}.bet-footer button,.bet-dialog>button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:9px 25px}.bet-mask{position:fixed;inset:0;z-index:30;display:grid;place-items:center;background:#0007}.bet-dialog{width:min(90vw,360px);padding:15px;background:#fff}.bet-dialog header{display:flex;justify-content:space-between;margin:0 0 14px;font-size:16px}.bet-dialog header button{border:0;background:transparent;color:#f04141;font-size:25px}.bet-dialog input{width:100%;height:38px;border:1px solid #ddd;padding:0 10px}.bet-dialog p{color:#d32f2f}.bet-dialog>button{width:100%;margin-top:14px}.page-state,.page-notice{margin:10px;color:#777;text-align:center}.page-notice{color:#d32f2f}@media (min-width:641px){.pjx-page{max-width:640px;margin:auto;box-shadow:0 0 20px #0002}}
.toolbar-tools{font-size:initial}.toolbar-tools svg{width:18px;height:18px;fill:#fff}.toolbar-tools button{display:grid;width:23px;height:30px;place-items:center}.toolbar-tools img{width:18px;height:18px;filter:brightness(0) invert(1)}
/* Legacy PJX: period title has a hairline; the results strip ends with a taller separator. */
.draw-head{border-bottom:1px solid #e5e5e5}.draw-area{border-bottom:5px solid #f1f1f1}.period-bar{border-top:0}
.draw-balls{min-height:64px;padding-bottom:4px}.draw-slot{gap:0}.draw-slot i{margin-top:-1px}
.compact-history{max-height:none;overflow:visible}
.pjx-page{height:100vh;min-height:0;overflow:hidden;padding-bottom:0;display:flex;flex-direction:column}.pjx-scroll{min-height:0;flex:1;overflow-x:hidden;overflow-y:scroll;scrollbar-gutter:stable}
.pjx-page .bet-action{position:fixed;right:0;bottom:0;left:0;z-index:20;display:flex;max-width:640px;margin:auto;gap:10px;padding:8px 15px 16px;background:#fff}.pjx-page .bet-action button{height:39px;flex:1;border:0;border-radius:2px;background:#f04141;color:#fff}.pjx-page .bet-action .reset{flex:0 0 80px;background:#51cdcc}
.bet-confirm-mask{position:fixed;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;padding:14px;background:rgba(0,0,0,.42)}.bet-confirm-dialog{box-sizing:border-box;width:min(100%,480px);padding:0 15px 14px;background:#fff;color:#333;font-size:13px}.bet-confirm-dialog header{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;min-height:40px;border-bottom:1px solid #eee}.bet-confirm-dialog header b,.confirm-summary b{color:#f04141;font-weight:400}.bet-confirm-dialog header>span:nth-child(2){color:#333}.bet-confirm-dialog header button{justify-self:end;border:0;background:transparent;color:#ff545b;font-size:30px;font-weight:200;line-height:1}.confirm-quick{display:flex;align-items:center;justify-content:center;gap:8px;height:42px}.confirm-quick input{box-sizing:border-box;width:88px;height:30px;border:0;background:#f5f5f5;padding:0 8px;text-align:center;outline:none}.confirm-head,.confirm-row{display:grid;grid-template-columns:1.25fr .8fr 1fr .8fr;align-items:center;text-align:center}.confirm-head{height:34px;border-top:1px solid #eee;border-bottom:1px solid #eee}.confirm-head b{font-weight:600}.confirm-row{min-height:43px;border-bottom:1px solid #eee;color:#666}.confirm-row input{box-sizing:border-box;width:80px;height:30px;justify-self:center;border:0;background:#f5f5f5;text-align:center;outline:none}.confirm-row button{border:0;background:transparent;color:#ff545b;font-size:28px;font-weight:200;line-height:1}.confirm-summary{display:flex;justify-content:space-between;padding:12px 0;font-size:13px}.confirm-error{margin:8px 0 0;color:#d92424;text-align:center}.confirm-submit{width:100%;height:41px;border:0;border-radius:2px;background:#f04141;color:#fff;font-size:14px}.confirm-submit:disabled{background:#ccc;color:#fff}
.pjx-chat-tabs + .pjx-tabs{display:none}
/* 旧版 .chat-content 是 height:100% + position:absolute;top:0，相对内容区填满，
   不是相对视口定位。这里 .pjx-page 已是 flex column，用 flex:1 等价填满剩余高度，
   顺带不再依赖 tab 栏的像素高度。字号同旧版的 .8rem。 */
.pjx-chat-content{display:flex;min-height:0;flex:1;flex-direction:column;background:#f5f5f5;font-size:.8rem}
.pjx-chat-container{flex:1;min-height:0;overflow-y:auto;padding:0 15px}.pjx-msg-item{margin:8px 0}.pjx-msg-time{text-align:center;color:#ccc;font-size:11px}.pjx-chat-message{display:flex;align-items:flex-start}.pjx-chat-me{justify-content:flex-end}.pjx-chat-other{justify-content:flex-start}.pjx-chat-avatar{width:32px;height:32px;flex:0 0 32px;object-fit:cover}.pjx-chat-msg-content{position:relative;max-width:82%;margin:0 8px}.pjx-chat-nickname{margin:0 0 2px;color:#666}.pjx-chat-me .pjx-chat-nickname{text-align:right}.pjx-chat-msg-text,.pjx-bet-msg{margin:0;border-radius:3px;background:#fff;color:#111}.pjx-chat-msg-text{padding:7px 12px;line-height:18px}.pjx-chat-me .pjx-chat-msg-text,.pjx-chat-me .pjx-bet-msg{background:#9eea6a}.pjx-bet-msg>p{margin:0;padding:6px 12px;border-bottom:1px solid #e5e5e5}.pjx-bet-line{display:flex;align-items:center;gap:8px;padding:6px 12px}.pjx-bet-line span{flex:1}.pjx-bet-line b,.pjx-collect-card b{color:#f04141}.pjx-bet-line button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:3px 10px}.pjx-bet-line em{color:#999;font-style:normal}.pjx-msg-card{box-sizing:border-box;width:80%;margin:auto;border:1px solid #e5e5e5;border-radius:2px;background:#2fbc8b;color:#fff;text-align:center}.pjx-msg-card strong{display:block;padding:6px;border-bottom:1px solid #e5e5e5}.pjx-collect-gates{display:grid;grid-template-columns:repeat(10,1fr);gap:3px;padding:6px}.pjx-collect-gates span{display:grid;gap:4px;justify-items:center}.pjx-collect-gates i{font-style:normal;white-space:nowrap}.pjx-collect-gates b{display:grid;width:23px;height:23px;place-items:center;border-radius:2px;color:#fff}.pjx-stat-head,.pjx-stat-row{display:grid;grid-template-columns:1.2fr 1fr 1fr 1fr;padding:4px 9px;line-height:18px}.pjx-chat-footer{background:#fff}.pjx-chat-filters{display:flex;justify-content:space-around;padding:5px;background:#f5f5f5}.pjx-chat-filters button{border:1px dashed #ccc;background:transparent;color:#999;padding:2px 5px;font-size:11px}.pjx-chat-filters button.checked{border-color:#f04141;color:#f04141}.pjx-chat-send{display:flex;gap:8px;padding:12px 15px}.pjx-chat-send input{min-width:0;flex:1;border:0;border-radius:3px;background:#f5f5f5;padding:8px}.pjx-chat-send button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:0 16px}.pjx-chat-send button:disabled{background:#ccc}.pjx-cancel-mask{position:fixed;inset:0;z-index:80;display:grid;place-items:center;background:#0005}.pjx-cancel-dialog{width:250px;background:#fff}.pjx-cancel-dialog h2{margin:0;padding:18px 22px 10px;font-size:18px}.pjx-cancel-dialog p{margin:0;padding:0 22px 25px;color:#888}.pjx-cancel-dialog footer{display:flex;justify-content:flex-end;gap:24px;padding:10px 22px 16px}.pjx-cancel-dialog button{border:0;background:transparent;color:#f04141}
/* Legacy state 2: each gate is independently dimmed during the sealed period. */
.gate-board.is-sealed button{pointer-events:none}
.gate-board.is-sealed button::after{position:absolute;inset:0;z-index:3;background:rgba(0,0,0,.48);content:''}

/* Legacy state 3: replace normal gate content with original number and rank. */
.gate-board.is-drawn button{display:block;min-height:64px;padding:3px 7px;background:#1d603b;border-color:#609b7c;text-align:center}
.gate-board.is-drawn button>small,.gate-board.is-drawn button>i{display:none}
.gate-board.is-drawn strong{display:block;height:14px;line-height:14px;color:#8b9c91;font-weight:400}
.gate-draw-result{position:relative;display:flex;height:39px;align-items:flex-start;justify-content:flex-start;gap:5px}
.gate-board.is-drawn .gate-draw-result{z-index:4}
.gate-draw-result i{position:static!important;display:inline-grid!important;width:31px;height:31px;place-items:center;border-radius:5px;color:#fff;font-size:17px;font-style:normal}
.gate-draw-result small{display:block!important;color:#8b9c91!important;font-size:12px;white-space:nowrap}
.gate-draw-result b{position:absolute;right:0;color:#fff;font-size:13px;font-weight:600;white-space:nowrap}
.gate-draw-member{position:absolute;bottom:0;left:0;width:100%;color:#9aaa9e;font-size:12px;text-align:center}

/* Shared legacy confirmation-success treatment, matching FantanBetPage. */
.confirm-row input:disabled,.confirm-quick input:disabled{color:#999}
.confirm-success-check{justify-self:center;color:green;font-size:27px;font-weight:500;line-height:1}
.confirm-submit.success{background:green}
.bet-success-toast{position:fixed;right:8px;bottom:8px;left:8px;z-index:60;min-height:48px;display:flex;align-items:center;padding:0 12px;border-radius:2px;background:rgba(30,30,30,.92);color:#fff;font-size:14px}
.pjx-chat-me .pjx-chat-avatar{order:2}
.pjx-close-check-card{font-size:12px}.pjx-close-check-total{padding:6px;margin:0;border-bottom:1px solid #e5e5e5}.pjx-close-check-gate{padding:6px 8px;border-bottom:1px solid #e5e5e5}.pjx-close-check-gate:last-child{border-bottom:0}.pjx-close-check-gate p{margin:4px 0}.pjx-close-check-row{display:flex;justify-content:space-between;gap:6px;text-align:left}.pjx-close-check-row span{overflow-wrap:anywhere}.pjx-close-check-row time{flex-shrink:0}
.pjx-toolbar,.pjx-game-toolbar{display:none}
.pjx-toolbar-action-overlays{display:none}.pjx-toolbar-actions button{display:grid;box-sizing:border-box;width:20px;height:36px;place-items:center;border:0;background:transparent;padding:0;line-height:0;cursor:pointer}.pjx-toolbar-actions button svg{display:block}.pjx-tool-page{position:fixed;inset:0;z-index:90;background:#f4f4f4;color:#111}.pjx-tool-page header{display:grid;grid-template-columns:48px 1fr 48px;height:56px;align-items:center;background:#f04141;color:#fff;text-align:center}.pjx-tool-page header b{font-size:18px;font-weight:500}.pjx-tool-page header button{border:0;background:transparent;color:#fff}.pjx-tool-page header svg{width:22px;height:22px;fill:none;stroke:currentColor;stroke-width:32;stroke-linecap:round;stroke-linejoin:round}.pjx-tool-page main{position:absolute;top:56px;right:0;bottom:0;left:0;overflow-y:auto;padding:20px;background:#fff;font-size:14px;line-height:1.8}.pjx-menu-mask{position:fixed;inset:0;z-index:90;background:rgb(0 0 0 / 34%)}.pjx-game-menu{width:min(76vw,380px);min-height:100%;background:#f4f4f4;box-shadow:2px 0 5px #0004}.pjx-game-menu header{display:flex;height:56px;align-items:center;justify-content:space-between;padding:0 16px;background:#f04141;color:#fff}.pjx-game-menu header b{font-size:18px;font-weight:500}.pjx-game-menu header button,.pjx-game-menu header a{border:0;background:transparent;color:#fff;font-size:30px;text-decoration:none}.pjx-menu-user{display:flex;align-items:center;gap:12px;padding:16px;background:#fff}.pjx-menu-user img{width:56px;height:56px;border-radius:50%;object-fit:cover}.pjx-menu-user div{display:grid;gap:5px}.pjx-menu-user small{color:#888}.pjx-menu-money{display:grid;grid-template-columns:1fr 1fr;border-top:8px solid #f4f4f4;background:#fff}.pjx-menu-money a{padding:13px;color:#333;text-align:center;text-decoration:none}.pjx-game-menu nav{margin-top:8px}.pjx-game-menu nav a{display:flex;height:48px;align-items:center;margin-top:1px;padding:0 16px;background:#fff;color:#333;text-decoration:none}.pjx-game-menu nav i{margin-left:auto;color:#aaa;font-size:22px;font-style:normal}.pjx-sound-tip{position:fixed;bottom:24px;left:50%;z-index:100;transform:translateX(-50%);border-radius:3px;padding:9px 14px;background:rgb(0 0 0 / 75%);color:#fff;font-size:13px}
.pjx-stat-money{color:#f04141}.pjx-stat-loss{color:#fff100}
.pjx-bet-msg-detail{display:flex;align-items:center}.pjx-bet-lines{min-width:0;flex:1}.pjx-bet-msg-detail .pjx-bet-line{padding-right:4px}.pjx-bet-action{flex:0 0 auto;padding:0 12px}.pjx-bet-action button{border:0;border-radius:2px;background:#f04141;color:#fff;padding:5px 11px}.pjx-bet-action button:disabled{background:#ccc}
</style>
